import {
  Clock3,
  HandCoins,
  LoaderCircle,
  RefreshCw,
  Scale,
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  Wallet,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import DetalleGastoModal from '../components/gastos/DetalleGastoModal'
import DetalleIngresoModal from '../components/ingresos/DetalleIngresoModal'
import CobrosPagos from '../components/dashboard/CobrosPagos'
import GastosPorCategoria from '../components/dashboard/GastosPorCategoria'
import KpiCard from '../components/dashboard/KpiCard'
import RankingTerceros from '../components/dashboard/RankingTerceros'
import UltimosMovimientos from '../components/dashboard/UltimosMovimientos'
import VentasCobrosGastos from '../components/dashboard/VentasCobrosGastos'
import { useEmpresa } from '../context/EmpresaContext'
import { supabase } from '../lib/supabase'
import {
  formatearInicioMes,
  generarMesesDelAnio,
  generarUltimosMeses,
  nombreMesCorto,
  obtenerAnioMesActual,
  obtenerClaveMes,
  obtenerClaveMesDesdeFecha,
  obtenerMesAnterior,
} from '../utils/fechas'
import { formatearMontoGasto } from '../utils/gastos'
import { obtenerFechaActualPos } from '../utils/pos'

const CAMPOS_GASTO = `
  id,
  empresa_id,
  categoria_id,
  proveedor_id,
  fecha_gasto,
  fecha_vencimiento,
  fecha_pago,
  tipo_documento,
  numero_documento,
  descripcion,
  monto_neto,
  monto_exento,
  monto_iva,
  otros_impuestos,
  monto_total,
  medio_pago,
  estado_pago,
  estado_registro,
  observaciones,
  creado_en,
  actualizado_en,
  categoria:categorias_gasto!fk_gasto_categoria_empresa (
    id,
    nombre
  ),
  proveedor:proveedores!fk_gasto_proveedor_empresa (
    id,
    razon_social,
    rut
  )
`

const CAMPOS_INGRESO = `
  id,
  empresa_id,
  cliente_id,
  fecha_ingreso,
  fecha_vencimiento,
  fecha_pago,
  tipo_documento,
  numero_documento,
  descripcion,
  monto_neto,
  monto_iva,
  monto_exento,
  otros_impuestos,
  monto_total,
  medio_pago,
  estado_pago,
  estado_registro,
  observaciones,
  creado_por,
  creado_en,
  actualizado_en,
  cliente:clientes!fk_ingreso_cliente_empresa (
    id,
    razon_social,
    rut
  )
`

const PERIODOS = [
  { valor: 'mes', etiqueta: 'Este mes' },
  { valor: '3meses', etiqueta: 'Últimos 3 meses' },
  { valor: '6meses', etiqueta: 'Últimos 6 meses' },
  { valor: 'anio', etiqueta: 'Este año' },
]

function calcularRangoMeses(periodo) {
  const { anio, mes } = obtenerAnioMesActual()

  if (periodo === 'mes') return generarUltimosMeses(1, anio, mes)
  if (periodo === '3meses')
    return generarUltimosMeses(3, anio, mes)
  if (periodo === 'anio') return generarMesesDelAnio(anio, mes)

  return generarUltimosMeses(6, anio, mes)
}

function calcularFechaInicioConsulta(periodo) {
  const { anio, mes } = obtenerAnioMesActual()
  const mesAnterior = obtenerMesAnterior(anio, mes)
  const mesesPeriodo = calcularRangoMeses(periodo)
  const primerMesPeriodo = mesesPeriodo[0]

  const claveOrden = (item) => item.anio * 12 + item.mes

  const mesMasAntiguo =
    claveOrden(primerMesPeriodo) <= claveOrden(mesAnterior)
      ? primerMesPeriodo
      : mesAnterior

  return formatearInicioMes(mesMasAntiguo.anio, mesMasAntiguo.mes)
}

// A diferencia de calcularFechaInicioConsulta (que amplía el rango para
// incluir el mes anterior y poder calcular variaciones de gastos/ingresos),
// las métricas de vw_dashboard_financiero_diario deben reflejar
// exactamente el período elegido, sin ese mes extra de contexto.
function calcularFechaInicioPeriodo(periodo) {
  const mesesPeriodo = calcularRangoMeses(periodo)
  const primerMesPeriodo = mesesPeriodo[0]

  return formatearInicioMes(
    primerMesPeriodo.anio,
    primerMesPeriodo.mes
  )
}

function sumarCampoVista(filas, campo) {
  return filas.reduce(
    (acumulado, fila) => acumulado + (Number(fila[campo]) || 0),
    0
  )
}

// Agrega las filas diarias de vw_dashboard_financiero_diario del período
// seleccionado. La vista ya excluye ventas POS anuladas y documentos
// anulados de ingresos/gastos, así que aquí solo se suma.
function calcularResumenFinanciero(filasVista) {
  const ventasPos = sumarCampoVista(filasVista, 'ventas_pos')
  const cobradoPos = sumarCampoVista(filasVista, 'cobrado_pos')
  const porCobrarPos = sumarCampoVista(filasVista, 'por_cobrar_pos')
  const otrosIngresos = sumarCampoVista(filasVista, 'otros_ingresos')
  const otrosIngresosCobrados = sumarCampoVista(
    filasVista,
    'otros_ingresos_cobrados'
  )
  const gastos = sumarCampoVista(filasVista, 'gastos')
  const gastosPagados = sumarCampoVista(filasVista, 'gastos_pagados')
  const flujoNeto = sumarCampoVista(filasVista, 'flujo_neto')
  const cantidadVentasPos = sumarCampoVista(
    filasVista,
    'cantidad_ventas_pos'
  )

  return {
    ventasPos,
    cantidadVentasPos,
    cobradoPos,
    porCobrarPos,
    cobradoTotal: cobradoPos + otrosIngresosCobrados,
    otrosIngresos,
    otrosIngresosCobrados,
    gastos,
    gastosPagados,
    flujoNeto,
  }
}

function calcularEvolucionFinanciera(filasVista, periodo) {
  return calcularRangoMeses(periodo).map(
    ({ anio: anioMes, mes: mesMes }) => {
      const clave = obtenerClaveMes(anioMes, mesMes)

      const filasDelMes = filasVista.filter(
        (fila) => obtenerClaveMesDesdeFecha(fila.fecha) === clave
      )

      const ventasPos = sumarCampoVista(filasDelMes, 'ventas_pos')
      const cobradoPos = sumarCampoVista(filasDelMes, 'cobrado_pos')
      const otrosIngresosCobrados = sumarCampoVista(
        filasDelMes,
        'otros_ingresos_cobrados'
      )
      const gastosPagados = sumarCampoVista(
        filasDelMes,
        'gastos_pagados'
      )
      const flujoNeto = sumarCampoVista(filasDelMes, 'flujo_neto')

      return {
        clave,
        etiqueta: nombreMesCorto(anioMes, mesMes),
        ventasPos,
        cobrado: cobradoPos + otrosIngresosCobrados,
        gastosPagados,
        flujoNeto,
      }
    }
  )
}

function sumarTotal(lista) {
  return lista.reduce(
    (acumulado, item) => acumulado + (Number(item.monto_total) || 0),
    0
  )
}

function filtrarPorClaveMes(lista, campoFecha, clave) {
  return lista.filter(
    (item) => obtenerClaveMesDesdeFecha(item[campoFecha]) === clave
  )
}

function calcularVariacion(actual, anterior) {
  if (anterior === 0) return null

  return ((actual - anterior) / Math.abs(anterior)) * 100
}

function calcularRanking(lista, obtenerNombre, textoSinNombre, totalGeneral) {
  const mapa = new Map()

  lista.forEach((item) => {
    const nombre = obtenerNombre(item) || textoSinNombre
    const actual = mapa.get(nombre) || { total: 0, cantidad: 0 }

    mapa.set(nombre, {
      total: actual.total + (Number(item.monto_total) || 0),
      cantidad: actual.cantidad + 1,
    })
  })

  return Array.from(mapa.entries())
    .map(([nombre, valores]) => ({
      nombre,
      total: valores.total,
      cantidad: valores.cantidad,
      porcentaje:
        totalGeneral > 0 ? (valores.total / totalGeneral) * 100 : 0,
    }))
    .sort((a, b) => b.total - a.total)
}

function calcularDatosDashboard(
  gastos,
  ingresos,
  gastosPendientes,
  ingresosPendientes,
  periodo
) {
  const { anio, mes } = obtenerAnioMesActual()
  const claveMesActual = obtenerClaveMes(anio, mes)

  const mesAnterior = obtenerMesAnterior(anio, mes)
  const claveMesAnterior = obtenerClaveMes(
    mesAnterior.anio,
    mesAnterior.mes
  )

  const gastosMesActual = filtrarPorClaveMes(
    gastos,
    'fecha_gasto',
    claveMesActual
  )
  const gastosMesAnterior = filtrarPorClaveMes(
    gastos,
    'fecha_gasto',
    claveMesAnterior
  )
  const ingresosMesActual = filtrarPorClaveMes(
    ingresos,
    'fecha_ingreso',
    claveMesActual
  )
  const ingresosMesAnterior = filtrarPorClaveMes(
    ingresos,
    'fecha_ingreso',
    claveMesAnterior
  )

  const totalGastosMes = sumarTotal(gastosMesActual)
  const totalGastosMesAnterior = sumarTotal(gastosMesAnterior)
  const totalIngresosMes = sumarTotal(ingresosMesActual)
  const totalIngresosMesAnterior = sumarTotal(ingresosMesAnterior)

  const resultadoMes = totalIngresosMes - totalGastosMes
  const resultadoMesAnterior =
    totalIngresosMesAnterior - totalGastosMesAnterior

  const variacionIngresos = calcularVariacion(
    totalIngresosMes,
    totalIngresosMesAnterior
  )
  const variacionGastos = calcularVariacion(
    totalGastosMes,
    totalGastosMesAnterior
  )
  const variacionResultado = calcularVariacion(
    resultadoMes,
    resultadoMesAnterior
  )

  const pagadoMes = sumarTotal(
    gastosMesActual.filter((gasto) => gasto.estado_pago === 'pagado')
  )
  const cobradoMes = sumarTotal(
    ingresosMesActual.filter(
      (ingreso) => ingreso.estado_pago === 'pagado'
    )
  )

  // Para esta primera versión, "pendiente" y "parcial" consideran el
  // monto_total completo tanto en ingresos como en gastos, porque todavía
  // no existe un módulo de pagos/abonos parciales. Cuando exista, aquí se
  // deberá calcular únicamente el saldo pendiente real (monto_total menos
  // lo ya cobrado/pagado).
  const pendientePorCobrar = sumarTotal(ingresosPendientes)
  const pendientePorPagar = sumarTotal(gastosPendientes)

  const margenMes =
    totalIngresosMes > 0 ? (resultadoMes / totalIngresosMes) * 100 : 0

  const evolucion = calcularRangoMeses(periodo).map(
    ({ anio: anioMes, mes: mesMes }) => {
      const clave = obtenerClaveMes(anioMes, mesMes)

      const ingresosDelMes = sumarTotal(
        filtrarPorClaveMes(ingresos, 'fecha_ingreso', clave)
      )
      const gastosDelMes = sumarTotal(
        filtrarPorClaveMes(gastos, 'fecha_gasto', clave)
      )

      return {
        clave,
        etiqueta: nombreMesCorto(anioMes, mesMes),
        ingresos: ingresosDelMes,
        gastos: gastosDelMes,
        resultado: ingresosDelMes - gastosDelMes,
      }
    }
  )

  const categoriasMapa = new Map()

  gastosMesActual.forEach((gasto) => {
    const nombre = gasto.categoria?.nombre || 'Sin categoría'
    const actual = categoriasMapa.get(nombre) || 0

    categoriasMapa.set(
      nombre,
      actual + (Number(gasto.monto_total) || 0)
    )
  })

  const categoriasGasto = Array.from(categoriasMapa.entries())
    .map(([nombre, total]) => ({ nombre, total }))
    .sort((a, b) => b.total - a.total)

  const rankingClientes = calcularRanking(
    ingresosMesActual,
    (ingreso) => ingreso.cliente?.razon_social,
    'Sin cliente',
    totalIngresosMes
  )

  const rankingProveedores = calcularRanking(
    gastosMesActual,
    (gasto) => gasto.proveedor?.razon_social,
    'Sin proveedor',
    totalGastosMes
  )

  const movimientos = [
    ...gastos.map((gasto) => ({
      id: gasto.id,
      tipo: 'gasto',
      fecha: gasto.fecha_gasto,
      creadoEn: gasto.creado_en,
      tercero: gasto.proveedor?.razon_social || 'Sin proveedor',
      descripcion: gasto.descripcion,
      monto: Number(gasto.monto_total) || 0,
      original: gasto,
    })),

    ...ingresos.map((ingreso) => ({
      id: ingreso.id,
      tipo: 'ingreso',
      fecha: ingreso.fecha_ingreso,
      creadoEn: ingreso.creado_en,
      tercero: ingreso.cliente?.razon_social || 'Sin cliente',
      descripcion: ingreso.descripcion,
      monto: Number(ingreso.monto_total) || 0,
      original: ingreso,
    })),
  ]
    .sort((a, b) => {
      if (a.fecha !== b.fecha) return a.fecha < b.fecha ? 1 : -1

      return (a.creadoEn || '') < (b.creadoEn || '') ? 1 : -1
    })
    .slice(0, 8)

  return {
    totalIngresosMes,
    totalGastosMes,
    resultadoMes,
    variacionIngresos,
    variacionGastos,
    variacionResultado,
    mesAnteriorInfo: mesAnterior,
    pagadoMes,
    cobradoMes,
    pendientePorCobrar,
    pendientePorPagar,
    margenMes,
    evolucion,
    categoriasGasto,
    rankingClientes,
    rankingProveedores,
    movimientos,
  }
}

function Dashboard() {
  const { empresaActual } = useEmpresa()
  const navigate = useNavigate()

  const [gastos, setGastos] = useState([])
  const [ingresos, setIngresos] = useState([])
  const [gastosPendientes, setGastosPendientes] = useState([])
  const [ingresosPendientes, setIngresosPendientes] = useState([])
  const [filasFinancieras, setFilasFinancieras] = useState([])
  const [totalHistoricoGastos, setTotalHistoricoGastos] =
    useState(0)
  const [totalHistoricoIngresos, setTotalHistoricoIngresos] =
    useState(0)
  const [totalHistoricoVentasPos, setTotalHistoricoVentasPos] =
    useState(0)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [periodo, setPeriodo] = useState('6meses')

  const [movimientoDetalle, setMovimientoDetalle] = useState(null)
  const [detalleAbierto, setDetalleAbierto] = useState(false)

  const esConsulta = empresaActual?.rol === 'consulta'

  const montadoRef = useRef(true)

  useEffect(() => {
    montadoRef.current = true

    return () => {
      montadoRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!empresaActual?.id) {
      setGastos([])
      setIngresos([])
      setGastosPendientes([])
      setIngresosPendientes([])
      setFilasFinancieras([])
      setTotalHistoricoGastos(0)
      setTotalHistoricoIngresos(0)
      setTotalHistoricoVentasPos(0)
      setCargando(false)
      cerrarDetalle()
      return
    }

    cargarDashboard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id, periodo])

  const cargarDashboard = async () => {
    if (!empresaActual?.id) return

    try {
      setCargando(true)
      setError('')

      const fechaInicioConsulta =
        calcularFechaInicioConsulta(periodo)
      const fechaInicioPeriodo = calcularFechaInicioPeriodo(periodo)
      const fechaHastaPeriodo = obtenerFechaActualPos()

      const [
        respuestaGastos,
        respuestaIngresos,
        respuestaGastosPendientes,
        respuestaIngresosPendientes,
        respuestaConteoGastos,
        respuestaConteoIngresos,
        respuestaFinanciera,
        respuestaConteoVentasPos,
      ] = await Promise.all([
        supabase
          .from('gastos')
          .select(CAMPOS_GASTO)
          .eq('empresa_id', empresaActual.id)
          .neq('estado_registro', 'anulado')
          .gte('fecha_gasto', fechaInicioConsulta)
          .order('fecha_gasto', { ascending: false })
          .order('creado_en', { ascending: false }),

        supabase
          .from('ingresos')
          .select(CAMPOS_INGRESO)
          .eq('empresa_id', empresaActual.id)
          .neq('estado_registro', 'anulado')
          .gte('fecha_ingreso', fechaInicioConsulta)
          .order('fecha_ingreso', { ascending: false })
          .order('creado_en', { ascending: false }),

        supabase
          .from('gastos')
          .select('id, monto_total, estado_pago')
          .eq('empresa_id', empresaActual.id)
          .neq('estado_registro', 'anulado')
          .in('estado_pago', ['pendiente', 'parcial']),

        supabase
          .from('ingresos')
          .select('id, monto_total, estado_pago')
          .eq('empresa_id', empresaActual.id)
          .neq('estado_registro', 'anulado')
          .in('estado_pago', ['pendiente', 'parcial']),

        supabase
          .from('gastos')
          .select('id', { count: 'exact', head: true })
          .eq('empresa_id', empresaActual.id)
          .neq('estado_registro', 'anulado'),

        supabase
          .from('ingresos')
          .select('id', { count: 'exact', head: true })
          .eq('empresa_id', empresaActual.id)
          .neq('estado_registro', 'anulado'),

        supabase
          .from('vw_dashboard_financiero_diario')
          .select(`
            fecha,
            ventas_pos,
            cobrado_pos,
            por_cobrar_pos,
            otros_ingresos,
            otros_ingresos_cobrados,
            gastos,
            gastos_pagados,
            flujo_neto,
            cantidad_ventas_pos
          `)
          .eq('empresa_id', empresaActual.id)
          .gte('fecha', fechaInicioPeriodo)
          .lte('fecha', fechaHastaPeriodo),

        supabase
          .from('vw_ventas_pos_resumen')
          .select('id', { count: 'exact', head: true })
          .eq('empresa_id', empresaActual.id)
          .eq('estado', 'registrado'),
      ])

      if (respuestaGastos.error) throw respuestaGastos.error
      if (respuestaIngresos.error) throw respuestaIngresos.error
      if (respuestaGastosPendientes.error)
        throw respuestaGastosPendientes.error
      if (respuestaIngresosPendientes.error)
        throw respuestaIngresosPendientes.error
      if (respuestaConteoGastos.error)
        throw respuestaConteoGastos.error
      if (respuestaConteoIngresos.error)
        throw respuestaConteoIngresos.error
      if (respuestaFinanciera.error) throw respuestaFinanciera.error
      if (respuestaConteoVentasPos.error)
        throw respuestaConteoVentasPos.error

      if (!montadoRef.current) return

      setGastos(respuestaGastos.data || [])
      setIngresos(respuestaIngresos.data || [])
      setGastosPendientes(respuestaGastosPendientes.data || [])
      setIngresosPendientes(respuestaIngresosPendientes.data || [])
      setFilasFinancieras(respuestaFinanciera.data || [])
      setTotalHistoricoGastos(respuestaConteoGastos.count || 0)
      setTotalHistoricoIngresos(respuestaConteoIngresos.count || 0)
      setTotalHistoricoVentasPos(
        respuestaConteoVentasPos.count || 0
      )
    } catch (errorCarga) {
      console.error('Error al cargar dashboard:', errorCarga)

      if (!montadoRef.current) return

      setError(
        errorCarga.message ||
          'No fue posible cargar el dashboard.'
      )
    } finally {
      if (montadoRef.current) {
        setCargando(false)
      }
    }
  }

  const datos = useMemo(
    () =>
      calcularDatosDashboard(
        gastos,
        ingresos,
        gastosPendientes,
        ingresosPendientes,
        periodo
      ),
    [gastos, ingresos, gastosPendientes, ingresosPendientes, periodo]
  )

  const resumenFinanciero = useMemo(
    () => calcularResumenFinanciero(filasFinancieras),
    [filasFinancieras]
  )

  const evolucionFinanciera = useMemo(
    () => calcularEvolucionFinanciera(filasFinancieras, periodo),
    [filasFinancieras, periodo]
  )

  const verMovimiento = (movimiento) => {
    setMovimientoDetalle(movimiento)
    setDetalleAbierto(true)
  }

  const cerrarDetalle = () => {
    setDetalleAbierto(false)
    setMovimientoDetalle(null)
  }

  const editarMovimiento = (id) => {
    setDetalleAbierto(false)

    if (movimientoDetalle?.tipo === 'ingreso') {
      navigate(`/ingresos/${id}/editar`)
    } else {
      navigate(`/gastos/${id}/editar`)
    }
  }

  const sinMovimientos =
    totalHistoricoGastos === 0 &&
    totalHistoricoIngresos === 0 &&
    totalHistoricoVentasPos === 0

  const colorFlujoNeto =
    resumenFinanciero.flujoNeto > 0
      ? 'text-emerald-600'
      : resumenFinanciero.flujoNeto < 0
        ? 'text-red-600'
        : 'text-slate-700'

  const renderContenido = () => {
    if (cargando) {
      return (
        <div className="flex min-h-96 items-center justify-center">
          <div className="text-center">
            <LoaderCircle
              size={38}
              className="mx-auto animate-spin text-emerald-600"
            />

            <p className="mt-4 font-medium text-slate-500">
              Cargando dashboard...
            </p>
          </div>
        </div>
      )
    }

    if (error) {
      return (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center shadow-sm">
          <p className="font-semibold text-red-700">
            No fue posible cargar el dashboard.
          </p>

          <p className="mt-1 text-sm text-red-600">{error}</p>

          <button
            type="button"
            onClick={cargarDashboard}
            className="mt-4 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white transition hover:bg-slate-900"
          >
            Reintentar
          </button>
        </div>
      )
    }

    if (sinMovimientos) {
      return (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <Scale
              size={28}
              className="text-emerald-600"
            />
          </div>

          <h3 className="mt-5 text-lg font-bold text-slate-900">
            Aún no hay movimientos registrados.
          </h3>

          <p className="mt-2 text-slate-500">
            Registra tu primer ingreso o gasto para comenzar a ver
            indicadores.
          </p>

          {!esConsulta && (
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/ingresos/nuevo"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white transition hover:bg-emerald-700"
              >
                Registrar primer ingreso
              </Link>

              <Link
                to="/gastos/nuevo"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-emerald-600 px-5 py-3 font-semibold text-emerald-700 transition hover:bg-emerald-50"
              >
                Registrar primer gasto
              </Link>
            </div>
          )}
        </div>
      )
    }

    return (
      <>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <KpiCard
            icono={ShoppingCart}
            etiqueta="Ventas POS"
            valor={formatearMontoGasto(resumenFinanciero.ventasPos)}
            ayuda={
              <p className="mt-2 text-xs font-medium text-slate-400">
                {resumenFinanciero.cantidadVentasPos}{' '}
                {resumenFinanciero.cantidadVentasPos === 1
                  ? 'venta'
                  : 'ventas'}
              </p>
            }
          />

          <KpiCard
            icono={Wallet}
            etiqueta="Cobrado"
            valor={formatearMontoGasto(
              resumenFinanciero.cobradoTotal
            )}
            ayuda={
              <p className="mt-2 text-xs font-medium text-slate-400">
                POS: {formatearMontoGasto(resumenFinanciero.cobradoPos)}
                {' · '}
                Otros:{' '}
                {formatearMontoGasto(
                  resumenFinanciero.otrosIngresosCobrados
                )}
              </p>
            }
          />

          <KpiCard
            icono={HandCoins}
            etiqueta="Por cobrar"
            valor={formatearMontoGasto(
              resumenFinanciero.porCobrarPos
            )}
            ayuda={
              <p className="mt-2 text-xs font-medium text-slate-400">
                Saldo pendiente de ventas POS del período
              </p>
            }
          />

          <KpiCard
            icono={TrendingUp}
            etiqueta="Otros ingresos"
            valor={formatearMontoGasto(
              resumenFinanciero.otrosIngresos
            )}
            ayuda={
              <p className="mt-2 text-xs font-medium text-slate-400">
                Cobrado:{' '}
                {formatearMontoGasto(
                  resumenFinanciero.otrosIngresosCobrados
                )}
              </p>
            }
          />

          <KpiCard
            icono={TrendingDown}
            etiqueta="Gastos"
            valor={formatearMontoGasto(resumenFinanciero.gastos)}
            ayuda={
              <p className="mt-2 text-xs font-medium text-slate-400">
                Pagado:{' '}
                {formatearMontoGasto(
                  resumenFinanciero.gastosPagados
                )}
              </p>
            }
          />

          <KpiCard
            icono={Scale}
            etiqueta="Flujo neto"
            valor={
              <span className={colorFlujoNeto}>
                {formatearMontoGasto(resumenFinanciero.flujoNeto)}
              </span>
            }
            ayuda={
              <p className="mt-2 text-xs font-medium text-slate-400">
                Cobros POS + otros ingresos cobrados − gastos
                pagados
              </p>
            }
          />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <KpiCard
            icono={Clock3}
            etiqueta="Otros por cobrar"
            valor={formatearMontoGasto(datos.pendientePorCobrar)}
            ayuda={
              <p className="mt-2 text-xs font-medium text-slate-400">
                Considera todos los otros ingresos pendientes de la
                empresa
              </p>
            }
          />

          <KpiCard
            icono={Clock3}
            etiqueta="Pendiente por pagar"
            valor={formatearMontoGasto(datos.pendientePorPagar)}
            ayuda={
              <p className="mt-2 text-xs font-medium text-slate-400">
                Considera todos los gastos pendientes de la
                empresa
              </p>
            }
          />
        </div>

        <div className="mt-6">
          <VentasCobrosGastos datos={evolucionFinanciera} />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
          <GastosPorCategoria
            categorias={datos.categoriasGasto}
            totalMes={datos.totalGastosMes}
          />

          <CobrosPagos
            cobradoPos={resumenFinanciero.cobradoPos}
            cobradoOtrosIngresos={
              resumenFinanciero.otrosIngresosCobrados
            }
            porCobrarPos={resumenFinanciero.porCobrarPos}
            pagadoMes={resumenFinanciero.gastosPagados}
            pendientePorPagar={datos.pendientePorPagar}
          />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
          <RankingTerceros
            titulo="Principales clientes del mes"
            subtitulo="Ordenados por total facturado."
            filas={datos.rankingClientes}
            etiquetaVacia="No hay ingresos registrados este mes."
            etiquetaCantidad="doc."
          />

          <RankingTerceros
            titulo="Principales proveedores del mes"
            subtitulo="Ordenados por total gastado."
            filas={datos.rankingProveedores}
            etiquetaVacia="No hay gastos registrados este mes."
            etiquetaCantidad="doc."
          />
        </div>

        <div className="mt-6">
          <UltimosMovimientos
            movimientos={datos.movimientos}
            onVer={verMovimiento}
          />
        </div>
      </>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
            Panel principal
          </p>

          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Dashboard
          </h2>

          <p className="mt-2 text-slate-500">
            Resumen financiero de{' '}
            {empresaActual?.nombre_fantasia}
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <select
            value={periodo}
            onChange={(event) => setPeriodo(event.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
          >
            {PERIODOS.map((opcion) => (
              <option key={opcion.valor} value={opcion.valor}>
                {opcion.etiqueta}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={cargarDashboard}
            disabled={cargando}
            className="flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              size={18}
              className={cargando ? 'animate-spin' : ''}
            />
            {cargando ? 'Actualizando...' : 'Actualizar'}
          </button>
        </div>
      </div>

      {renderContenido()}

      <DetalleGastoModal
        abierto={detalleAbierto && movimientoDetalle?.tipo === 'gasto'}
        gasto={
          movimientoDetalle?.tipo === 'gasto'
            ? movimientoDetalle.original
            : null
        }
        onCerrar={cerrarDetalle}
        onEditar={esConsulta ? undefined : editarMovimiento}
      />

      <DetalleIngresoModal
        abierto={
          detalleAbierto && movimientoDetalle?.tipo === 'ingreso'
        }
        ingreso={
          movimientoDetalle?.tipo === 'ingreso'
            ? movimientoDetalle.original
            : null
        }
        onCerrar={cerrarDetalle}
        onEditar={esConsulta ? undefined : editarMovimiento}
      />
    </div>
  )
}

export default Dashboard
